import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { FocusTrap } from "../a11y/FocusTrap";
import { Button } from "./Button";

type Size = "sm" | "md" | "lg" | "xl" | "full";
const sizes: Record<Size, string> = {
  sm:  "max-w-sm",
  md:  "max-w-lg",
  lg:  "max-w-2xl",
  xl:  "max-w-4xl",
  full: "max-w-[96vw] h-[92vh]",
};

/**
 * Accessible Modal / Dialog primitive.
 *
 *  • Focus-trapped (Tab cycles inside)
 *  • Esc closes
 *  • Click-outside closes (unless dismissible=false)
 *  • role="dialog" + aria-modal + aria-labelledby
 *  • Renders into #modal-root via portal
 *  • Auto-locks body scroll while open
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = "md",
  dismissible = true,
  footer,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: Size;
  dismissible?: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const titleId = useId();
  const descId  = useId();
  const initialRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [open]);

  useEffect(() => {
    if (!open || !dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  const host = typeof document !== "undefined"
    ? (document.getElementById("modal-root") ?? document.body)
    : null;
  if (!host) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-end justify-center bg-black/55 p-0 backdrop-blur-sm sm:items-center sm:p-6 animate-fade-in"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <FocusTrap active className="contents" initialFocusRef={initialRef as React.RefObject<HTMLElement>}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          className={cx(
            "relative w-full overflow-hidden rounded-t-xl bg-[var(--c-elevated)] shadow-modal",
            "sm:rounded-xl sm:border sm:border-default",
            "animate-scale-in flex max-h-[92vh] flex-col",
            sizes[size],
            className
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-default px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h2 id={titleId} className="truncate text-base font-semibold text-default">{title}</h2>
              {description && (
                <p id={descId} className="mt-0.5 text-xs text-muted">{description}</p>
              )}
            </div>
            {dismissible && (
              <Button
                ref={initialRef}
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Close dialog"
                onClick={onClose}
              >
                ✕
              </Button>
            )}
          </header>
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
            {children}
          </div>
          {footer && (
            <footer className="flex items-center justify-end gap-2 border-t border-default bg-[var(--c-surface-muted)] px-4 py-3 sm:px-5">
              {footer}
            </footer>
          )}
        </div>
      </FocusTrap>
    </div>,
    host
  );
}

/** Convenience: confirmation dialog */
export function ConfirmModal({
  open, onClose, onConfirm,
  title, description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger";
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{cancelLabel}</Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description && <p className="text-sm text-default">{description}</p>}
    </Modal>
  );
}
